import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import EyeHideBlue from 'assets/img/eye-hide-blue.svg';
import EyeShowBlue from 'assets/img/eye-show-blue.svg';
import classNames from 'classnames';

import lang from '../../lib/language';

import { IPasswordProps } from './PasswordControl.types';

import styles from './PasswordControl.module.scss';

function PasswordControl(
  {
    id,
    name,
    placeholder,
    value,
    label,
    ariaLabel,
    visibleHint = false,
    visibleAddon = false,
    onChange,
    onValueChange,
    validationFlags,
    onSubmit,
    isSubmitButtonClicked,
  }: IPasswordProps,
) {
  const [inputValue, setInputValue] = useState<string>(!value ? '' : value);
  const [toggledTextType, setToggledTextType] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isLabelFloated, setIsLabelFloated] = useState<boolean>(false);
  const [isPristine, setIsPristine] = useState<boolean>(true);

  const hasPasswordError = isSubmitButtonClicked && validationFlags
    ? !validationFlags.eightSymbols
    || !validationFlags.uppercaseLetter
    || !validationFlags.lowercaseLetter
    || !validationFlags.specialChar
    || !validationFlags.number
    || !validationFlags.onlyLatinCharacters
    : false;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const targetValue = event.target.value;
    setInputValue(targetValue);

    if (onChange) {
      onChange(event);
    }

    onValueChange && onValueChange(targetValue);
  };

  const handleFocus = useCallback((): void => {
    setIsFocused(true);
    setIsPristine(false);
    setIsLabelFloated(true);
  }, []);

  const handleBlur = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;

    let targetValue = target.value;

    const noWhitespacesCheck = targetValue.trim() !== '';

    setIsFocused(false);
    setIsLabelFloated(noWhitespacesCheck);

    if (!noWhitespacesCheck) {
      targetValue = '';
      setInputValue('');

      target.value = targetValue;

      onChange && onChange(event);
    }
  }, [onChange]);

  const getHintTextStyle = useCallback((isValid: boolean): string => {
    if (!isPristine) {
      return isValid ? styles.validRule : styles.invalidRule;
    }

    return '';
  }, [isPristine]);

  useEffect(() => {
    if (value) {
      setInputValue(value);
      setIsLabelFloated(true);
    } else {
      setIsLabelFloated(false);
    }

    return () => {
      setInputValue('');
    };
  }, [inputValue, value, setInputValue]);

  const handleOnKeyPress = (event: { key: any }) => {
    const { key } = event;
    key === 'Enter' && onSubmit?.();
  };

  return (
    <>
      <div className={classNames(styles.inputContainer, styles.inputContainerBg)}>
        <input
          id={id}
          aria-label={ariaLabel}
          type={toggledTextType ? 'text' : 'password'}
          autoComplete="off"
          name={name}
          value={inputValue}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyPress={handleOnKeyPress}
          className={classNames(styles.input, styles.inputControlBorder, {
            [styles.secondaryInput]: visibleAddon,
            [styles.inputErrorBorder]: hasPasswordError,
          })}
          placeholder={placeholder}
        />

        {label && (
          <label
            htmlFor={id}
            className={classNames(styles.label, { [styles.floatedLabel]: isLabelFloated || isFocused })}
          >
            {label}
          </label>
        )}

        {visibleAddon && (
          <button
            type="button"
            className={styles.groupAddon}
            onClick={() => setToggledTextType(!toggledTextType)}
          >
            <img
              alt="eye"
              className={styles.eyeImage}
              src={toggledTextType ? EyeHideBlue : EyeShowBlue}
            />
          </button>
        )}
      </div>
      {visibleHint && (
        <ul className={styles.hintsWrapper}>
          <li className={validationFlags && getHintTextStyle(validationFlags.number)}>
            {lang.commonOnboardingSignInValidationNumber()}
          </li>
          <li className={validationFlags && getHintTextStyle(validationFlags.uppercaseLetter)}>
            {lang.commonOnboardingSignInValidationUpperCaseLetter()}
          </li>
          <li className={validationFlags && getHintTextStyle(validationFlags.lowercaseLetter)}>
            {lang.commonOnboardingSignInValidationLowerCaseLetter()}
          </li>
          <li className={validationFlags && getHintTextStyle(validationFlags.specialChar)}>
            {lang.commonOnboardingSignInValidationSpecialSymbol()}
          </li>
          <li className={validationFlags && getHintTextStyle(validationFlags.eightSymbols)}>
            {lang.commonOnboardingSignInValidation8Chart()}
          </li>
        </ul>
      )}
    </>
  );
}

export default PasswordControl;
